import { PDFDocument, StandardFonts, rgb } from 'pdf-lib'

type TrendDirection = 'up' | 'down' | 'neutral'

type WaterMetricSnapshot = {
  current: number
  previous: number | null
  trend: number
  matchedLots: number
  totalLots: number
  unit: string
}

type DashboardReportInput = {
  generatedAtIso: string
  lastUpdatedLabel: string
  contextLabel: string
  kpis: {
    totalFishStock: number
    fishStockTrend: number
    activeDailyFeedKg: number
    activeDailyFeedTrendPercent: number
    actualDailyFeedKg: number
    actualDailyFeedTrendPercent: number
    actualDailyFeedEvents: number
    weeklyMortalityRatePercentLabel: string
    weeklyMortalityTrendValue: number
    weeklyMortalityTrendDirection: TrendDirection
    weeklyMortalityTrendDetail?: string
    growthRateLabel: string
    growthTrendValue: number
    growthTrendDirection: TrendDirection
    growthTrendDetail?: string
    water: {
      ph: WaterMetricSnapshot
      dissolvedOxygen: WaterMetricSnapshot
      salinity: WaterMetricSnapshot
      temperature: WaterMetricSnapshot
    }
    activeTasks: number | null
  }
  notes?: {
    dataError?: string
  }
}

const formatSigned = (value: number, decimals = 2): string => {
  if (!Number.isFinite(value)) return '-'
  const sign = value > 0 ? '+' : ''
  return `${sign}${value.toFixed(decimals)}`
}

const formatDirection = (dir: TrendDirection): string => {
  if (dir === 'up') return 'UP'
  if (dir === 'down') return 'DOWN'
  return 'FLAT'
}

const safeText = (value: unknown): string => {
  if (value === null || value === undefined) return '-'
  const text = String(value)
  return text.trim().length > 0 ? text : '-'
}

// Standard PDF fonts (Helvetica, etc) cannot encode arbitrary Unicode.
// Keep output strictly ASCII to avoid runtime exceptions.
const toPdfSafeAscii = (value: string): string => {
  return value
    .replaceAll('—', '-')
    .replaceAll('–', '-')
    .replaceAll('•', '-')
    .replaceAll('·', '-')
    .replaceAll('↑', 'UP')
    .replaceAll('↓', 'DOWN')
    .replaceAll('→', 'FLAT')
    .replaceAll('‰', 'ppt')
    .replaceAll('°', '')
    .replaceAll(/[\u0080-\uFFFF]/g, '')
}

type PdfFont = Awaited<ReturnType<PDFDocument['embedFont']>>

const wrapText = (font: PdfFont, fontSize: number, text: string, maxWidth: number): string[] => {
  const normalized = toPdfSafeAscii(text).replaceAll(/\s+/g, ' ').trim()
  if (!normalized) return ['-']

  const words = normalized.split(' ')
  const lines: string[] = []
  let current = ''

  const pushCurrent = () => {
    if (current.trim().length > 0) lines.push(current.trim())
    current = ''
  }

  const fits = (candidate: string) => font.widthOfTextAtSize(candidate, fontSize) <= maxWidth

  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word
    if (fits(candidate)) {
      current = candidate
      continue
    }

    // If the current line has content, push it and retry the word.
    if (current) {
      pushCurrent()
    }

    // If a single word is longer than maxWidth, split it.
    if (!fits(word)) {
      let remaining = word
      while (remaining.length > 0) {
        let sliceLength = remaining.length
        while (sliceLength > 1 && !fits(remaining.slice(0, sliceLength))) sliceLength -= 1
        const slice = remaining.slice(0, sliceLength)
        lines.push(slice)
        remaining = remaining.slice(sliceLength)
      }
      continue
    }

    current = word
  }

  pushCurrent()
  return lines.length > 0 ? lines : ['-']
}

const isoToUtcLabel = (iso: string): string => {
  // Expecting ISO-8601 input. Keep it ASCII and explicit about UTC.
  return toPdfSafeAscii(iso.replace('T', ' ').replace('Z', ' UTC'))
}

export async function buildDashboardReportPdf(input: DashboardReportInput): Promise<Uint8Array> {
  const pdfDoc = await PDFDocument.create()

  const fontRegular = await pdfDoc.embedFont(StandardFonts.Helvetica)
  const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold)

  const pageSize: [number, number] = [595.28, 841.89] // A4 in points
  let page = pdfDoc.addPage(pageSize)
  const { width, height } = page.getSize()

  const marginX = 48
  const marginY = 52
  const lineGap = 16

  let y = height - marginY

  const ensureSpace = (needed: number) => {
    if (y - needed >= marginY) return
    page = pdfDoc.addPage(pageSize)
    y = height - marginY
  }

  const drawLine = () => {
    ensureSpace(16)
    page.drawLine({
      start: { x: marginX, y },
      end: { x: width - marginX, y },
      thickness: 1,
      color: rgb(0.85, 0.87, 0.91)
    })
    y -= 16
  }

  const drawTextWrapped = (
    text: string,
    options?: {
      x?: number
      maxWidth?: number
      size?: number
      bold?: boolean
      color?: { r: number; g: number; b: number }
      extraGapAfter?: number
    }
  ) => {
    const size = options?.size ?? 11
    const bold = options?.bold ?? false
    const x = options?.x ?? marginX
    const maxWidth = options?.maxWidth ?? (width - marginX - x)
    const color = options?.color ?? { r: 0.11, g: 0.13, b: 0.17 }
    const font = bold ? fontBold : fontRegular

    const lines = wrapText(font, size, text, maxWidth)
    for (const line of lines) {
      ensureSpace(size + 6)
      page.drawText(line, {
        x,
        y,
        size,
        font,
        color: rgb(color.r, color.g, color.b)
      })
      y -= size + 6
    }
    if (options?.extraGapAfter) y -= options.extraGapAfter
  }

  const drawKeyValueWrapped = (key: string, value: string) => {
    const keyText = toPdfSafeAscii(`${key}:`)
    const valueX = marginX + 170
    const valueMaxWidth = width - marginX - valueX
    const valueLines = wrapText(fontRegular, 11, value, valueMaxWidth)

    ensureSpace(lineGap)
    page.drawText(keyText, {
      x: marginX,
      y,
      size: 11,
      font: fontBold,
      color: rgb(0.11, 0.13, 0.17)
    })

    // First line aligned with the key.
    page.drawText(valueLines[0] ?? '-', {
      x: valueX,
      y,
      size: 11,
      font: fontRegular,
      color: rgb(0.11, 0.13, 0.17)
    })
    y -= lineGap

    // Continuation lines (value only).
    for (let i = 1; i < valueLines.length; i += 1) {
      ensureSpace(lineGap)
      page.drawText(valueLines[i], {
        x: valueX,
        y,
        size: 11,
        font: fontRegular,
        color: rgb(0.11, 0.13, 0.17)
      })
      y -= lineGap
    }
  }

  // Header
  drawTextWrapped('Sunday App - Dashboard Report', { size: 18, bold: true, extraGapAfter: 2 })
  drawTextWrapped('Technical snapshot of operational KPIs for the selected context.', { size: 11, color: { r: 0.42, g: 0.45, b: 0.5 } })
  drawTextWrapped('Timestamps are ISO-8601 in UTC.', { size: 10, color: { r: 0.42, g: 0.45, b: 0.5 }, extraGapAfter: 6 })
  drawLine()

  drawKeyValueWrapped('Generated', isoToUtcLabel(input.generatedAtIso))
  drawKeyValueWrapped('Dashboard last updated', isoToUtcLabel(safeText(input.lastUpdatedLabel)))
  drawKeyValueWrapped('Context', safeText(input.contextLabel))

  if (input.notes?.dataError) {
    drawTextWrapped('Data Refresh Notice', { size: 12, bold: true, color: { r: 0.56, g: 0.11, b: 0.11 } })
    drawTextWrapped(safeText(input.notes.dataError), { size: 11, color: { r: 0.56, g: 0.11, b: 0.11 }, extraGapAfter: 6 })
  }

  drawLine()

  // KPI Summary
  drawTextWrapped('KPI Summary', { size: 14, bold: true, extraGapAfter: 2 })
  drawKeyValueWrapped('Total active fish stock', `${safeText(input.kpis.totalFishStock)} fish (${formatSigned(input.kpis.fishStockTrend, 2)} vs last month)`) 
  drawKeyValueWrapped('Recommended daily feed', `${input.kpis.activeDailyFeedKg.toFixed(2)} kg (${formatSigned(input.kpis.activeDailyFeedTrendPercent, 2)}% vs last week)`) 
  drawKeyValueWrapped('Actual daily feed (events)', (() => {
    const adherence = input.kpis.activeDailyFeedKg > 0
      ? (input.kpis.actualDailyFeedKg / Math.max(input.kpis.activeDailyFeedKg, 1)) * 100
      : NaN
    const adherenceLabel = Number.isFinite(adherence) ? `, ${Math.round(adherence)}% adherence` : ''
    return `${input.kpis.actualDailyFeedKg.toFixed(2)} kg (${formatSigned(input.kpis.actualDailyFeedTrendPercent, 2)}% vs last week, ${input.kpis.actualDailyFeedEvents} events${adherenceLabel})`
  })())
  drawKeyValueWrapped('Weekly mortality rate', `${safeText(input.kpis.weeklyMortalityRatePercentLabel)}% ${formatDirection(input.kpis.weeklyMortalityTrendDirection)} ${formatSigned(input.kpis.weeklyMortalityTrendValue, 2)} vs last week ${input.kpis.weeklyMortalityTrendDetail ? input.kpis.weeklyMortalityTrendDetail : ''}`.trim())
  drawKeyValueWrapped('Growth rate', `${safeText(input.kpis.growthRateLabel)} ${formatDirection(input.kpis.growthTrendDirection)} ${formatSigned(input.kpis.growthTrendValue, 2)} ${input.kpis.growthTrendDetail ? input.kpis.growthTrendDetail : ''}`.trim())
  drawKeyValueWrapped('Active tasks', input.kpis.activeTasks === null ? '-' : String(input.kpis.activeTasks))

  drawLine()

  // Quick Analysis
  drawTextWrapped('Quick Analysis (what we can infer from this snapshot)', { size: 14, bold: true, extraGapAfter: 2 })
  const totalLots = input.kpis.water.ph.totalLots
  const lowCoverage = (matched: number) => totalLots > 0 && matched / totalLots < 0.5
  const waterCoverageSummary = `Water coverage (lots with readings in last 7 days): pH ${input.kpis.water.ph.matchedLots}/${totalLots}, DO ${input.kpis.water.dissolvedOxygen.matchedLots}/${totalLots}, salinity ${input.kpis.water.salinity.matchedLots}/${totalLots}, temp ${input.kpis.water.temperature.matchedLots}/${totalLots}.`
  drawTextWrapped(`- ${waterCoverageSummary}${(lowCoverage(input.kpis.water.ph.matchedLots) || lowCoverage(input.kpis.water.salinity.matchedLots) || lowCoverage(input.kpis.water.dissolvedOxygen.matchedLots) || lowCoverage(input.kpis.water.temperature.matchedLots)) ? ' Low coverage can cause missing or unstable averages.' : ''}`, { size: 11 })
  drawTextWrapped(`- Fish stock: ${safeText(input.kpis.totalFishStock)} fish (${formatSigned(input.kpis.fishStockTrend, 2)} vs last month).`, { size: 11 })
  drawTextWrapped(`- Mortality change: ${formatDirection(input.kpis.weeklyMortalityTrendDirection)} ${formatSigned(input.kpis.weeklyMortalityTrendValue, 2)} vs last week (current rate ${safeText(input.kpis.weeklyMortalityRatePercentLabel)}%).`, { size: 11 })
  drawTextWrapped(`- Growth change: ${formatDirection(input.kpis.growthTrendDirection)} ${formatSigned(input.kpis.growthTrendValue, 2)} (label ${safeText(input.kpis.growthRateLabel)}).`, { size: 11 })
  drawTextWrapped((() => {
    const adherence = input.kpis.activeDailyFeedKg > 0
      ? (input.kpis.actualDailyFeedKg / Math.max(input.kpis.activeDailyFeedKg, 1)) * 100
      : NaN
    const adherenceLabel = Number.isFinite(adherence) ? `, ${Math.round(adherence)}% adherence` : ''
    return `- Feed: Recommended ${input.kpis.activeDailyFeedKg.toFixed(2)} kg/day (${formatSigned(input.kpis.activeDailyFeedTrendPercent, 2)}% vs last week) vs Actual ${input.kpis.actualDailyFeedKg.toFixed(2)} kg/day (${formatSigned(input.kpis.actualDailyFeedTrendPercent, 2)}% vs last week, ${input.kpis.actualDailyFeedEvents} events${adherenceLabel}).`
  })(), { size: 11 })
  drawTextWrapped(`- Active tasks: ${input.kpis.activeTasks === null ? '-' : String(input.kpis.activeTasks)}. Use Tasks to confirm priorities and owners.`, { size: 11, extraGapAfter: 6 })

  drawLine()

  // Water Quality
  drawTextWrapped('Water Quality (7-day average, latest reading per lot)', { size: 14, bold: true, extraGapAfter: 4 })

  const drawWaterRow = (label: string, metric: WaterMetricSnapshot) => {
    const current = metric.matchedLots > 0 ? metric.current : NaN
    const trend = metric.previous !== null && metric.matchedLots > 0 ? metric.trend : NaN
    const coverage = toPdfSafeAscii(`${metric.matchedLots}/${metric.totalLots} lots`)
    const unit = metric.unit ? ` ${toPdfSafeAscii(metric.unit)}` : ''

    ensureSpace(lineGap)
    page.drawText(toPdfSafeAscii(label), { x: marginX, y, size: 11, font: fontBold, color: rgb(0.11, 0.13, 0.17) })
    page.drawText(Number.isFinite(current) ? `${current.toFixed(label === 'Temperature' ? 1 : 2)}${unit}` : '-', {
      x: marginX + 180,
      y,
      size: 11,
      font: fontRegular,
      color: rgb(0.11, 0.13, 0.17)
    })
    page.drawText(Number.isFinite(trend) ? `${formatSigned(trend, 2)}${unit}` : '-', {
      x: marginX + 310,
      y,
      size: 11,
      font: fontRegular,
      color: rgb(0.11, 0.13, 0.17)
    })
    page.drawText(coverage, {
      x: marginX + 430,
      y,
      size: 11,
      font: fontRegular,
      color: rgb(0.42, 0.45, 0.5)
    })
    y -= lineGap
  }

  // Table header
  ensureSpace(2 * lineGap)
  page.drawText('Metric', { x: marginX, y, size: 10, font: fontBold, color: rgb(0.42, 0.45, 0.5) })
  page.drawText('Current', { x: marginX + 180, y, size: 10, font: fontBold, color: rgb(0.42, 0.45, 0.5) })
  page.drawText('Trend', { x: marginX + 310, y, size: 10, font: fontBold, color: rgb(0.42, 0.45, 0.5) })
  page.drawText('Coverage', { x: marginX + 430, y, size: 10, font: fontBold, color: rgb(0.42, 0.45, 0.5) })
  y -= 12

  drawWaterRow('pH', input.kpis.water.ph)
  drawWaterRow('Dissolved Oxygen', input.kpis.water.dissolvedOxygen)
  drawWaterRow('Salinity', input.kpis.water.salinity)
  drawWaterRow('Temperature', input.kpis.water.temperature)

  drawLine()

  // Notes
  drawTextWrapped('Computation Notes', { size: 14, bold: true, extraGapAfter: 2 })
  drawTextWrapped('- Water metrics are computed as 7-day averages using the latest reading per lot; trend compares current 7 days vs previous 7 days.', { size: 11 })
  drawTextWrapped('- Recommended feed and fish stock are computed from the dashboard production dataset for the selected context.', { size: 11 })
  drawTextWrapped('- Actual feed is computed from FeedLog events ("Actual fed") stored offline-first and synced to the API.', { size: 11 })
  drawTextWrapped('- Values shown are operational indicators; investigate raw rows in Water / Database screens for audits and corrections.', { size: 11 })

  return await pdfDoc.save()
}
